import React, { useMemo, useCallback, useState, useRef, useEffect } from "react";
import "./FemaleLevelPage.css";
import { useTranslation } from "react-i18next";
import TopBar2 from "../common/TopBar2";

import FemaleLevelHero from "./FemaleLevelHero";
import FemaleLevelCarousel from "./FemaleLevelCarousel";
import FemaleLevelActions from "./FemaleLevelActions";
import FemaleWithdrawCard from "./FemaleWithdrawCard";

import useFemaleProfile from "../../hooks/useFemaleProfile";
import useFemaleLevelRules from "../../hooks/useFemaleLevelRules";
import { formatNumberEn } from "../../utils/digits";

function normalizeLevels(rules) {
  if (!Array.isArray(rules?.levels) || !rules.levels.length) {
    return [
      { level: 0, requiredTokens: 0, requiredDays: 0 },
      { level: 1, requiredTokens: 100, requiredDays: 0 },
      { level: 2, requiredTokens: 1500, requiredDays: 5 },
      { level: 3, requiredTokens: 4000, requiredDays: 5 },
      { level: 4, requiredTokens: 10000, requiredDays: 5 },
      { level: 5, requiredTokens: 25000, requiredDays: 5 },
      { level: 6, requiredTokens: 70000, requiredDays: 6 },
      { level: 7, requiredTokens: 150000, requiredDays: 6 },
      { level: 8, requiredTokens: 300000, requiredDays: 7 },
      { level: 9, requiredTokens: 550000, requiredDays: 7 },
      { level: 10, requiredTokens: 1000000, requiredDays: 7 },
    ];
  }

  return [...rules.levels]
    .map((item) => ({
      level: Number(item?.level ?? 0),
      requiredTokens: Number(item?.requiredTokens ?? 0),
      requiredDays: Number(item?.requiredDays ?? 0),
    }))
    .sort((a, b) => a.level - b.level);
}

function buildProgressData(profile, rules) {
  const levels = normalizeLevels(rules);

  const currentLevel = Number(profile?.femaleLevel ?? 0);
  const cycleTokens = Number(profile?.leveling?.tokens ?? 0);
  const activeDays = Number(profile?.leveling?.activeDays ?? 0);
  const availableTokens = Number(profile?.earnings?.availableTokens ?? 0);

  const maxLevel = levels.length ? levels[levels.length - 1].level : 0;

  const currentRule =
    levels.find((item) => item.level === currentLevel) || levels[0];

  const nextRule =
    levels.find((item) => item.level === currentLevel + 1) || currentRule;

  const isMaxLevel = currentLevel >= maxLevel;

  const targetTokens = isMaxLevel
    ? Number(currentRule?.requiredTokens ?? 0)
    : Number(nextRule?.requiredTokens ?? 0);

  const requiredDays =
    currentLevel <= 0 || isMaxLevel
      ? 0
      : Number(nextRule?.requiredDays ?? 0);

  const daysLeft =
    requiredDays > 0 ? Math.max(0, requiredDays - activeDays) : 0;

  const tokenProgressPercent =
    targetTokens > 0
      ? Math.max(0, Math.min(100, (cycleTokens / targetTokens) * 100))
      : 0;

  const isReadyByTokens = isMaxLevel ? false : cycleTokens >= targetTokens;
  const isReadyByDays = requiredDays <= 0 ? true : activeDays >= requiredDays;

  return {
    levels,
    maxLevel,
    currentLevel,
    nextLevel: isMaxLevel ? currentLevel : currentLevel + 1,
    currentRule,
    nextRule,
    cycleTokens,
    targetTokens,
    activeDays,
    requiredDays,
    daysLeft,
    tokenProgressPercent,
    remainingTokens: Math.max(0, targetTokens - cycleTokens),
    isReady: !isMaxLevel && isReadyByTokens && isReadyByDays,
    isMaxLevel,
    availableTokens,
  };
}

const PREVIEW_LEVEL_DURATION_MS = 2800;

export default function FemaleLevelPage() {
  const { t } = useTranslation();
  const { profile, loading } = useFemaleProfile();
  const { rules, loading: rulesLoading } = useFemaleLevelRules();

  const [previewLevel, setPreviewLevel] = useState(null);
  const previewTimerRef = useRef(null);

  const clearPreviewTimer = useCallback(() => {
    if (previewTimerRef.current) {
      clearTimeout(previewTimerRef.current);
      previewTimerRef.current = null;
    }
  }, []);

  const schedulePreviewClear = useCallback(() => {
    clearPreviewTimer();
    previewTimerRef.current = setTimeout(() => {
      setPreviewLevel(null);
      previewTimerRef.current = null;
    }, PREVIEW_LEVEL_DURATION_MS);
  }, [clearPreviewTimer]);

  useEffect(() => {
    return () => clearPreviewTimer();
  }, [clearPreviewTimer]);

  const handleBack = useCallback(() => {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      window.location.assign("/");
    }
  }, []);

  const progressData = useMemo(() => {
    if (!profile) return null;
    return buildProgressData(profile, rules || {});
  }, [profile, rules]);

  const realLevel = progressData?.currentLevel ?? Number(profile?.femaleLevel ?? 0);

  const handlePreviewLevelSelect = useCallback(
    (level) => {
      const lv = Number(level);
      if (!Number.isFinite(lv)) return;

      if (lv === realLevel) {
        clearPreviewTimer();
        setPreviewLevel(null);
        return;
      }

      setPreviewLevel(lv);
      schedulePreviewClear();
    },
    [realLevel, clearPreviewTimer, schedulePreviewClear]
  );

  if (loading || rulesLoading || !profile || !progressData) {
    return (
      <div className="femaleLevelPage loadingState">
        {t("common.loading", { defaultValue: "Loading..." })}
      </div>
    );
  }

  const displayLevel =
    previewLevel !== null ? Number(previewLevel) : progressData.currentLevel;

  const selectedRule =
    progressData.levels.find((item) => item.level === displayLevel) || null;

  return (
    <div className="femaleLevelPage">
      <TopBar2
        title={t("female_level.overview.title", { defaultValue: "My level" })}
        onBack={handleBack}
      />

      <div className="femaleLevelPageInner">
        <FemaleLevelHero
          profile={profile}
          displayLevel={displayLevel}
          progressData={progressData}
        />

        <div className="femaleLevelSectionCard">
          <div className="femaleLevelSectionHeader">
            <h3>
              {t("female_level.overview.weekProgress", {
                defaultValue: "طھظ‚ط¯ظ…ظƒ ظپظٹ ط§ظ„ظ…ط³طھظˆظ‰",
              })}
            </h3>
          </div>

          <div className="femaleLevelStatsGrid">
            <div className="femaleStatBox">
              <span>
                {t("female_level.progress.tokens", {
                  defaultValue: "طھظˆظƒظ† ط§ظ„طھظ‚ط¯ظ… ط§ظ„ط­ط§ظ„ظٹ",
                })}
              </span>
              <strong>{formatNumberEn(progressData.cycleTokens)}</strong>
            </div>

            <div className="femaleStatBox">
              <span>
                {t("female_level.progress.activeDays", {
                  defaultValue: "ط£ظٹط§ظ… ط§ظ„ظ†ط´ط§ط·",
                })}
              </span>
              <strong>
                {progressData.currentLevel === 0 || progressData.requiredDays <= 0
                  ? t("female_level.requirements.noDaysForLevelZero", {
                      defaultValue: "ط¨ط¯ظˆظ† ط´ط±ط· ط²ظ…ظ†ظٹ",
                    })
                  : `${formatNumberEn(progressData.activeDays)} / ${formatNumberEn(
                      progressData.requiredDays
                    )}`}
              </strong>
            </div>

            <div className="femaleStatBox">
              <span>
                {t("female_level.status.label", {
                  defaultValue: "ط§ظ„ط­ط§ظ„ط©",
                })}
              </span>
              <strong>
                {progressData.isMaxLevel
                  ? t("female_level.status.maxLevel", {
                      defaultValue: "ط£ط¹ظ„ظ‰ ظ…ط³طھظˆظ‰",
                    })
                  : progressData.isReady
                  ? t("female_level.status.ready", {
                      defaultValue: "ط¬ط§ظ‡ط²ط© ظ„ظ„طھط±ظ‚ظٹط©",
                    })
                  : t("female_level.status.inProgress", {
                      defaultValue: "ط¬ط§ط±ظٹ ط§ظ„طھظ‚ط¯ظ…",
                    })}
              </strong>
            </div>
          </div>
        </div>

        <FemaleLevelCarousel
          levels={progressData.levels}
          realLevel={progressData.currentLevel}
          previewLevel={previewLevel}
          onSelectLevel={handlePreviewLevelSelect}
        />

        <FemaleLevelActions />

        <FemaleWithdrawCard
          balance={progressData.availableTokens}
          minWithdraw={2000}
          currency={t("tokens.unit", {
            defaultValue: "Token",
          })}
        />

        {selectedRule && (
          <div className="femaleLevelSectionCard">
            <div className="femaleLevelSectionHeader">
              <h3>
                {t("female_level.overview.selectedLevel", {
                  defaultValue: "طھظپط§طµظٹظ„ ط§ظ„ظ…ط³طھظˆظ‰",
                })}{" "}
            {t("female_level.overview.levelLabel", {
              defaultValue: "Lv{{level}}",
              level: selectedRule.level,
            })}
              </h3>
            </div>

            <div className="femaleSelectedLevelInfo">
              <div className="femaleInfoRow">
                <span>
                  {t("female_level.requirements.requiredTokens", {
                    defaultValue: "ط§ظ„طھظˆظƒظ† ط§ظ„ظ…ط·ظ„ظˆط¨",
                  })}
                </span>
                <strong>{formatNumberEn(selectedRule.requiredTokens)}</strong>
              </div>

              <div className="femaleInfoRow">
                <span>
                  {t("female_level.requirements.requiredDays", {
                    defaultValue: "ط£ظٹط§ظ… ط§ظ„ظ†ط´ط§ط· ط§ظ„ظ…ط·ظ„ظˆط¨ط©",
                  })}
                </span>
                <strong>
                  {selectedRule.level <= 1
                    ? t("female_level.requirements.noDaysForLevelZero", {
                        defaultValue: "ط¨ط¯ظˆظ† ط´ط±ط· ط²ظ…ظ†ظٹ",
                      })
                    : formatNumberEn(selectedRule.requiredDays)}
                </strong>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
